import React, { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import constants from "../shared/constants";
import errorConstants from "../shared/constants/error";
import middleware from "../shared/middleware";
import { removeLocalStorage, getLocalStorage } from "../shared/utils/localStorage";
import { shouldRetryRefresh, reloadApp } from "../shared/utils";
import storageConstants from "../shared/constants/storage";
import moment from "moment";

import {
  registerKeepAliveServiceWorker,
  updateKeepAliveServiceWorkerConfig,
  clearKeepAliveServiceWorkerConfig,
  setupManualServiceWorkerKeepAlive,
  requestImmediateKeepAlive,
} from "../shared/utils/serviceWorkerKeepAlive";

import { setCookie } from "../shared/utils/cookie";
import { setLocalStorage } from "../shared/utils/localStorage";

const { extendUserSession, videoStreamHeartBeat } = middleware;
const {
  SELECTED_CHANNEL,
  ACTIVE_PROFILE_USER_ID,
  USER_PROPERTY_TYPE,
  AVS_REFRESH_TOKEN,
  SELECTED_TIME,
  USER_HAS_LOGGED_IN,
} = storageConstants;

const { APP_ENVIRONMENTS, API_BASE_URL, USER_SESSION_RENEWAL_INTERVAL } = constants;
const { AVS_ERROR_CODES } = errorConstants;

const KeepAliveManager = (props) => {
  const { appProvider, videoPlaying, userProfile, keepAliveDelay } = props;

  const renewUserSessionTimerId = useRef();
  const manualSwKeepAliveCleanup = useRef(null);
  const fallbackHB = useRef(false);
  const videoPlayerPayload = useRef();
  let performKeepAlive;

  const removeUserSession = useCallback(() => {
    removeLocalStorage(AVS_REFRESH_TOKEN);
    removeLocalStorage(ACTIVE_PROFILE_USER_ID);
    removeLocalStorage(USER_PROPERTY_TYPE);
    removeLocalStorage(SELECTED_CHANNEL);
    removeLocalStorage(SELECTED_TIME);
    removeLocalStorage(USER_HAS_LOGGED_IN);
  }, []);

  const resetAppSession = useCallback(() => {
    if (renewUserSessionTimerId.current) {
      removeUserSession();
      const ENV = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;
      clearInterval(renewUserSessionTimerId.current);
      // We only want to force appside logout situation in Prod when the session becomes too stale to renew.

      removeLocalStorage(SELECTED_CHANNEL);
      removeLocalStorage("selectedCategories");
      const logoutRedirectUrl = appProvider.config.general.telus_logout_url;
      window.location.href = logoutRedirectUrl.replace(
        "$redirectURI",
        encodeURIComponent(appProvider.config.general.AVS_config.redirectUri)
      );
    }
  }, [appProvider, removeUserSession]);

  const buildRenewUrl = useCallback(() => {
    let url = appProvider?.config?.avs_api_urls?.user_session_renewal;
    if (!url) return null;

    url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
    url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
    url = url.replace("$LANG", appProvider.AVS_LANG);
    url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
    url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

    const env = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;
    if (env === APP_ENVIRONMENTS.DEV) {
      url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
    }

    return url;
  }, [appProvider]);

  const retryTokenFetch = useCallback(
    (result, avsRefreshToken) => {
      if (shouldRetryRefresh(result?.data?.errorDescription) && avsRefreshToken === false) {
        // Attempt to renew and fetch a new token
        if (fallbackHB.current) {
          performKeepAlive(true);
        } else {
          requestImmediateKeepAlive(true);
        }
      } else if (avsRefreshToken) {
        // If we fail to renew throw user into guest mode
        resetAppSession();
      } else if (result?.data?.errorDescription === AVS_ERROR_CODES.INVALID_STREAM_SESSION) {
        reloadApp(appProvider);
      } /*else if (result?.data?.resultCode !== "OK" && quickPollEnabled.current === false) {
        // Assume the service is down and poll ever 1 min
        setupKeepAlive(CASSANDRA_KEEPALIVE_INTERVAL);
      } else if (result?.data?.resultCode === "OK" && quickPollEnabled.current === true) {
        // Revert back to normal polling
        setupKeepAlive();
      }*/
    },
    [resetAppSession, appProvider, performKeepAlive]
  );

  const callHeartbeat = useCallback(
    (avsRefreshToken = false) => {
      const avsRFToken = getLocalStorage(AVS_REFRESH_TOKEN);

      extendUserSession(appProvider, avsRefreshToken ? avsRFToken : null)
        .then((res) => {
          retryTokenFetch(res, avsRefreshToken);
        })
        .catch((err) => {
          retryTokenFetch(err, avsRefreshToken);
          console.error(err);
        });
    },
    [appProvider, retryTokenFetch]
  );

  const processResponse = useCallback(
    (jsonData) => {
      if (jsonData?.data) {
        // Store new avs-refresh-token
        if (jsonData.data?.headers?.["avs-refresh-token"]) {
          setCookie(AVS_REFRESH_TOKEN, jsonData.data.headers[AVS_REFRESH_TOKEN], 365);
          setLocalStorage(AVS_REFRESH_TOKEN, jsonData.data.headers[AVS_REFRESH_TOKEN]);
        }
        retryTokenFetch(jsonData, jsonData?.useAVSRefreshToken ?? false);
      }
    },
    [retryTokenFetch]
  );

  const processResponseFailure = useCallback(
    (jsonData) => {
      if (jsonData?.data) {
        retryTokenFetch(jsonData, jsonData?.useAVSRefreshToken ?? false);
      }
    },
    [retryTokenFetch]
  );

  const renewTokens = useCallback(
    (avsRefreshToken) => {
      callHeartbeat(avsRefreshToken);
    },
    [callHeartbeat]
  );

  appProvider.refreshToken = renewTokens;

  performKeepAlive = useCallback(
    (avsRefreshToken = false) => {
      const avsRFToken = getLocalStorage(AVS_REFRESH_TOKEN);

      if (
        videoPlayerPayload.current.playing &&
        videoPlayerPayload.current.currentContentId &&
        videoPlayerPayload.current.assetId &&
        avsRFToken
      ) {
        videoStreamHeartBeat(
          appProvider,
          videoPlayerPayload.current.type,
          videoPlayerPayload.current.currentContentId,
          videoPlayerPayload.current.assetId,
          null,
          null,
          avsRFToken
        )
          .then((res) => {
            retryTokenFetch(res, avsRefreshToken);
          })
          .catch((err) => {
            retryTokenFetch(err, avsRefreshToken);
            console.error(err);
          });
      } else {
        callHeartbeat(avsRefreshToken);
      }
    },
    [appProvider, callHeartbeat, retryTokenFetch]
  );

  const setupKeepAlive = useCallback(
    (delayOverride = null) => {
      if (userProfile?.isLoggedIn) {
        clearInterval(renewUserSessionTimerId.current);
        renewUserSessionTimerId.current = setInterval(() => {
          performKeepAlive();
        }, (delayOverride ? delayOverride : keepAliveDelay) * 1000);
      }
    },
    [performKeepAlive, userProfile, keepAliveDelay]
  );

  useEffect(() => {
    if (fallbackHB.current) {
      videoPlayerPayload.current = videoPlaying;
    }
  }, [videoPlaying]);

  // Register Service Worker for background keep-alive on login
  useEffect(() => {
    console.log("[KeepAliveManager] SW registration effect running, isLoggedIn:", userProfile?.isLoggedIn);
    if (userProfile?.isLoggedIn && fallbackHB.current === false) {
      console.log("[KeepAliveManager] Attempting to register Service Worker...");
      registerKeepAliveServiceWorker().catch((error) => {
        // Clean up manual SW interval before falling back to setInterval
        if (manualSwKeepAliveCleanup.current) {
          manualSwKeepAliveCleanup.current();
          manualSwKeepAliveCleanup.current = null;
        }
        fallbackHB.current = true;
        setupKeepAlive();
        console.log("[KeepAliveManager] Service Worker registration failed:", error);
        return;
      });
      const renewUrl = buildRenewUrl();
      const refreshToken = getLocalStorage(AVS_REFRESH_TOKEN);
      const isLegacy = true;
      console.log(
        "[KeepAliveManager] Sending config to SW, renewUrl:",
        renewUrl ? "present" : "missing",
        "token:",
        refreshToken ? "present" : "missing"
      );

      updateKeepAliveServiceWorkerConfig(processResponse, processResponseFailure, {
        renewUrl,
        refreshToken,
        videoPlaying,
        isLegacy,
      }).catch((error) => {
        console.warn("[KeepAliveManager] Failed to update Service Worker config:", error);
      });

      if (manualSwKeepAliveCleanup.current === null) {
        manualSwKeepAliveCleanup.current = setupManualServiceWorkerKeepAlive(600); // In Legacy flow we're always required to send a 10 min HB
      }
    } else {
      clearKeepAliveServiceWorkerConfig().catch((error) => {
        console.warn("[KeepAliveManager] Failed to clear Service Worker config:", error);
      });
    }
  }, [userProfile, buildRenewUrl, videoPlaying, processResponse, processResponseFailure, setupKeepAlive]);

  return <></>;
};

function mapStateToProps({ app }) {
  return {
    videoPlaying: app.videoPlaying,
    appProvider: app.provider,
    userProfile: app.userProfile,
  };
}

KeepAliveManager.propTypes = {
  appProvider: PropTypes.object,
  videoPlaying: PropTypes.object,
  userProfile: PropTypes.object,
};

export default connect(mapStateToProps)(KeepAliveManager);
